import { useEffect, useRef } from "react";
import { UseInfiniteQueryResult, UseQueryResult } from "@tanstack/react-query";

// types similar to @tanstack/react-query types
export type OnSuccessCallback<TData> = (response: TData) => void;
export type OnErrorCallback<TError> = (error: TError) => void;
export type OnSettledCallback<TData, TError> = (data: TData | undefined, error: TError | null) => void;

type SetCallbacksFunc<TData, TError> = (callbacks: {
  onSuccess?: OnSuccessCallback<TData>;
  onError?: OnErrorCallback<TError>;
  onSettled?: OnSettledCallback<TData, TError>;
}) => void;

type UseCallbackRefsReturnType<TData, TError> = {
  setRequestCallbacks: SetCallbacksFunc<TData, TError>;
};

type UseRequestCallbacksGeneralHandlers<TData, TError> = {
  onSuccess?: OnSuccessCallback<TData>;
  onError?: OnErrorCallback<TError>;
  onSettled?: OnSettledCallback<TData, TError>;
};

type UseRequestCallbacksOptions<TData, TError> = UseQueryResult<TData, TError> | UseInfiniteQueryResult<TData, TError>;

export const useRequestCallbacks = <TData, TError>(
  queryResult: UseRequestCallbacksOptions<TData, TError>,
  { onSuccess, onError, onSettled }: UseRequestCallbacksGeneralHandlers<TData, TError> = {}
): UseCallbackRefsReturnType<TData, TError> => {
  const onSuccessCallbackRef = useRef<OnSuccessCallback<TData> | undefined>(undefined);
  const onErrorCallbackRef = useRef<OnErrorCallback<TError> | undefined>(undefined);
  const onSettledCallbackRef = useRef<OnSettledCallback<TData, TError> | undefined>(undefined);

  const onSuccessCallbackCalledRef = useRef(false);
  const onErrorCallbackCalledRef = useRef(false);
  const onSettledCallbackCalledRef = useRef(false);

  const { data, isSuccess, isError, error } = queryResult;

  const onSuccessHandler = (data: TData) => {
    if (onSuccessCallbackCalledRef.current) return;

    onSuccessCallbackRef.current?.(data as TData);
    onSuccess?.(data as TData);
    onSuccessCallbackCalledRef.current = true;
  };

  const onErrorHandler: OnErrorCallback<TError> = (error) => {
    if (onErrorCallbackCalledRef.current) return;

    onErrorCallbackRef.current?.(error);
    onError?.(error);
    onErrorCallbackCalledRef.current = true;
  };

  const onSettledHandler: OnSettledCallback<TData, TError> = (data, error) => {
    if (onSettledCallbackCalledRef.current) return;

    onSettledCallbackRef.current?.(data, error);
    onSettled?.(data, error);
    onSettledCallbackCalledRef.current = true;
  };

  const setRequestCallbacks: SetCallbacksFunc<TData, TError> = (callbacks) => {
    onSuccessCallbackRef.current = callbacks.onSuccess;
    onErrorCallbackRef.current = callbacks.onError;
    onSettledCallbackRef.current = callbacks.onSettled;

    onSuccessCallbackCalledRef.current = false;
    onErrorCallbackCalledRef.current = false;
    onSettledCallbackCalledRef.current = false;
  };

  useEffect(() => {
    if (isError && error) {
      onSettledHandler(undefined, error);
      onErrorHandler(error);
      return;
    }

    if (isSuccess && data) {
      onSettledHandler(data, null);
      onSuccessHandler(data);
    }
  }, [data, isSuccess, isError]);

  return { setRequestCallbacks };
};
